import * as Sentry from "@sentry/react-router";
import { triggerGracefulReload } from "./graceful-reload";

/**
 * Error patterns that indicate a version mismatch between client and server.
 * These errors occur when:
 * - A new version is deployed while users have open tabs
 * - The client's cached JavaScript expects a different response format
 * - React Router's turbo-stream response cannot be decoded
 */
const VERSION_MISMATCH_PATTERNS = [
	/Unable to decode turbo-stream response/i,
	/manifest version mismatch/i,
	/Failed to fetch dynamically imported module/i,
	/Loading chunk .* failed/i,
	/error loading dynamically imported module/i,
	// Safari/WebKit-specific wording for the same dynamic-import failure
	// (Chrome says "Failed to fetch dynamically imported module";
	//  Safari says "Importing a module script failed.")
	/Importing a module script failed/i,
	/^Load failed$/i,
];

const STALE_CHUNK_URL_PATTERN = /\/assets\/[^/?#]+\.(js|css)(?:[?#]|$)/i;

interface ResourceLoadTarget {
	readonly tagName?: string;
	readonly src?: string;
	readonly href?: string;
}

/**
 * Checks whether a URL points at a Vite content-hashed asset that 404s after deploy.
 */
export function isStaleChunkResourceUrl(url: string): boolean {
	try {
		return STALE_CHUNK_URL_PATTERN.test(
			new URL(url, window.location.href).pathname,
		);
	} catch {
		return false;
	}
}

function isVersionMismatchMessage(message: string): boolean {
	return VERSION_MISMATCH_PATTERNS.some((pattern) => pattern.test(message));
}

function isStaleChunkScriptError(event: Event): boolean {
	const target = (event as ErrorEvent).target as ResourceLoadTarget | null;
	if (!target) return false;
	const tag = (target.tagName ?? "").toLowerCase();
	if (tag !== "script" && tag !== "link") return false;
	const url = target.src ?? target.href ?? "";
	if (!url) return false;
	return isStaleChunkResourceUrl(url);
}

/**
 * Checks if an error is caused by a version mismatch between client and server.
 */
export function isVersionMismatchError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	return isVersionMismatchMessage(error.message);
}

/**
 * Handles version mismatch by triggering a full page reload.
 * Uses a session storage flag to prevent infinite reload loops.
 */
export function handleVersionMismatch(error: Error): void {
	triggerGracefulReload(error, {
		category: "version-mismatch",
		storageKey: "version-mismatch-reload",
	});
}

/**
 * Sets up a global error handler to catch version mismatch errors during navigation.
 * This catches errors that occur outside of React's error boundary (e.g., in fetch calls).
 */
export function setupVersionMismatchHandler(): void {
	// Handle unhandled promise rejections (e.g., failed fetches during navigation)
	window.addEventListener("unhandledrejection", (event) => {
		const error = event.reason;
		if (isVersionMismatchError(error)) {
			event.preventDefault();
			handleVersionMismatch(error);
		}
	});

	// Handle uncaught errors (e.g., syntax errors in dynamically imported modules)
	window.addEventListener("error", (event) => {
		const error = event.error;
		if (error instanceof Error && isVersionMismatchError(error)) {
			event.preventDefault();
			handleVersionMismatch(error);
			return;
		}
		const message = (event as ErrorEvent).message ?? "";
		if (!error && message && isVersionMismatchMessage(message)) {
			event.preventDefault();
			handleVersionMismatch(new Error(message));
			return;
		}
		if (!error && isStaleChunkScriptError(event)) {
			event.preventDefault();
			const target = (event as ErrorEvent).target as ResourceLoadTarget;
			const url = target.src ?? target.href ?? "unknown";
			handleVersionMismatch(new Error(`Stale chunk failed to load: ${url}`));
		}
	});
}
